import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { AuthenticationService } from '@treasury/domain/services/authentication';
import { LogoutManager } from '@treasury/domain/services/logout';
import { openDialog } from '@treasury/omega';
import { TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-checkbox';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import TermsAndConditionsClient from './client';
import { type TermsAndConditions } from './terms-conditions.types';

export const tagName = 'terms-and-conditions';
const title = `Terms & Conditions`;

@customElement(tagName)
export class TermsAndConditionsContainer extends TmBaseComponent {
    @property({ type: Boolean })
    public loading = true;

    @property({ type: Object })
    public termsAndConditions: TermsAndConditions = {} as TermsAndConditions;

    @property({ type: Boolean })
    public showAcceptanceDate = false;

    @state()
    private client = new TermsAndConditionsClient();

    @state()
    private isAccepted = false;

    @InjectProperty()
    declare private readonly authService: AuthenticationService;

    @InjectProperty()
    declare private readonly logoutManager: LogoutManager;

    @InjectProperty()
    declare private readonly navService: NavigationService;

    public async connectedCallback() {
        super.connectedCallback();
        const result: TermsAndConditions = await this.client.getTermsAndConditions();
        const needsToAgree =
            !result.lastAcceptedDate ||
            new Date(result.lastAcceptedDate) < new Date(result.updatedDate);
        if (result && result.text && needsToAgree) {
            this.termsAndConditions = result;
            // UX rule:
            // Regardless of the current state,
            // any time the user is sent to this view, they must Agree again to proceed.
            this.isAccepted = false;
            this.loading = false;
        } else {
            // Make sure that the user can get to the dashboard if there are no terms and conditions.
            sessionStorage.setItem('termsAndConditions', 'true');
            this.authService.authenticate();
            this.navService.navigate(`/dashboard`);
        }
    }

    protected render() {
        if (this.loading) {
            return nothing;
        }

        return html`<form class="view">
            ${this.renderTermsAndConditions()} ${this.renderEula()}
            <div class="agreement-block">
                <tm-button importance="primary" @click=${this.handleAcceptClick}>
                    Accept
                </tm-button>
                <tm-button class="ml-2" @click=${this.handleCancelClick}> Cancel </tm-button>
            </div>
        </form>`;
    }

    private handleDownload(e: CustomEvent<{ downloadType: string }>) {
        this.client.downloadTermsAndConditions(e.detail.downloadType);
    }

    private handlePrint() {
        window.print();
    }

    private async handleAcceptClick() {
        await this.client.acceptTermsAndConditions();
        sessionStorage.setItem('termsAndConditions', 'true');

        // TODO: Handle save error from the API?
        this.navService.navigate(`/dashboard`);
    }

    private formattedLastAcceptedDate() {
        // TODO: implement this in fi-datetime and use it here.
        const date = new Date(this.termsAndConditions.lastAcceptedDate);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const dayOfMonth = String(date.getDate()).padStart(2, '0');
        return `${month}/${dayOfMonth}/${year}`;
    }

    private async handleCancelClick() {
        const response = await openDialog(document.createElement('omega-dialog'), {
            title: 'Confirm Cancel',
            content: html`<div style="margin: 20px">
                <p>
                    If you do not accept the Terms and Conditions, <br />
                    you will not be able to proceed and <br />
                    your session will be terminated.
                </p>
            </div>`,
            buttons: [
                { type: 'primary', label: `OK`, result: 'ok' },
                { type: 'secondary', label: `Return`, result: 'cancel' },
            ],
        });

        if (response === 'ok') {
            this.client.declineTermsAndConditions();
            await this.logoutManager.logOut('Forced');
            this.navService.navigate(`/login`);
        }
    }

    private renderLastAcceptedOn() {
        if (!this.showAcceptanceDate) {
            return ``;
        }
        if (!this.termsAndConditions?.lastAcceptedDate) {
            return ``;
        }

        return html` <div class="body-coda">
            <strong>Accepted On: ${this.formattedLastAcceptedDate()} </strong>
        </div>`;
    }

    private renderTermsAndConditions() {
        this.termsAndConditions.text = this.termsAndConditions.text?.replace(/style="(.*?)"/g, '');
        return html`
            <div>
                <div>
                    <omega-action-bar
                        .title="${title}"
                        .downloadOptions=${['PDF']}
                        @download="${this.handleDownload}"
                        @print="${this.handlePrint}"
                    ></omega-action-bar>
                </div>
                <div class="terms">${unsafeHTML(this.termsAndConditions.text)}</div>
            </div>
        `;
    }

    private renderEula() {
        this.termsAndConditions.endUserLicenseAgreementText?.replace(/style="(.*?)"/g, '');
        return html`
            <br />
            <br />
            <br />
            <br />
            <div>
                <div class="eula">
                    ${unsafeHTML(this.termsAndConditions.endUserLicenseAgreementText)}
                </div>
                ${this.renderLastAcceptedOn()}
            </div>
        `;
    }

    public static get styles() {
        return [
            css`
                :host {
                    height: 100%;
                    overflow: auto;
                }
                .view {
                    padding: 15px;
                    color: var(--primary-text-color);
                    background-color: var(--container-primary);
                }
                .terms h3,
                .eula h3 {
                    font-weight: 500;
                }
                .terms p,
                .eula p {
                    margin-top: 1em;
                    font-size: 0.875rem;
                    line-height: 1.25rem;
                }
                .terms ul,
                .terms ol,
                .eula ul,
                .eula ol {
                    margin: 1rem 0;
                    padding: 0 0 0 1rem;
                }
                .terms li,
                .eula li {
                    margin-top: 1rem;
                    font-size: 0.875rem;
                    line-height: 1.25rem;
                }
                .agreement-block {
                    padding-left: 24px;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TermsAndConditionsContainer;
    }
}
